import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const hasAccepted = localStorage.getItem("ama-cookies-accepted");
    if (!hasAccepted) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("ama-cookies-accepted", "true");
    localStorage.setItem("ama-cookies-accepted-date", new Date().toISOString());
    setIsClosing(true);
    setTimeout(() => setIsVisible(false), 300);
  };

  const handleDecline = () => {
    // Still set that user made a choice, but with declined status
    localStorage.setItem("ama-cookies-accepted", "essential-only");
    localStorage.setItem("ama-cookies-accepted-date", new Date().toISOString());
    setIsClosing(true);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 transition-all duration-300 ${
        isClosing ? "translate-y-full opacity-0" : "translate-y-0 opacity-100"
      }`}
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white border border-ink-200 rounded-2xl shadow-2xl shadow-black/50 p-6 backdrop-blur-lg">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            {/* Icon and Text */}
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-ink-950" />
              </div>
              <div className="space-y-2">
                <h3 className="text-ink-950 font-semibold text-lg">We value your privacy</h3>
                <p className="text-ink-500 text-sm leading-relaxed">
                  We use cookies to enhance your browsing experience, serve personalized content, 
                  and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. 
                  Read our{" "}
                  <Link to="/privacy" className="text-coral-500 hover:text-violet-300 underline">
                    Privacy Policy
                  </Link>
                  {" "}and{" "}
                  <Link to="/cookies" className="text-coral-500 hover:text-violet-300 underline">
                    Cookie Policy
                  </Link>
                  {" "}to learn more.
                </p>
                <p className="text-ink-500 text-xs italic">
                  Scroll down to view our Privacy Policy and Terms of Service in the footer.
                </p>
              </div>
            </div>
            
            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={handleDecline}
                className="px-5 py-2.5 text-ink-700 hover:text-ink-950 border border-ink-300 hover:border-slate-500 rounded-xl transition-all text-sm font-medium"
                data-testid="cookie-decline-btn"
              >
                Essential Only
              </button>
              <button
                onClick={handleAccept}
                className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-ink-950 rounded-xl transition-all text-sm font-medium shadow-lg shadow-violet-500/25"
                data-testid="cookie-accept-btn"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
